import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { endpoit } from "./URLs";
import fallbackImage from "../assets/images/news-placeholder.jpg";

export const NEWS_TYPE_ID = 10140;

const langId = (lang) => (lang === "ar" ? 2 : 1);

const fetchV2 = async (endpoint, params) => {
  const response = await axios.get(process.env.REACT_APP_BASE_URL_V2 + endpoint, { params });
  return response.data;
};

// { articleType, articles } for article category 10140
export const useNews = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return useQuery({
    queryKey: [`news${lang}`, lang],
    queryFn: () =>
      fetchV2(endpoit.GetArticlesByType, { articleTypeId: NEWS_TYPE_ID, langId: langId(lang) }),
    // Highest rank first; equal ranks keep the API's order
    select: (data) => ({
      ...data,
      articles: [...(data?.articles ?? [])].sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0)),
    }),
  });
};

// Articles whose `path` is a link (YouTube included) open it in a new tab instead of the news page.
export const getExternalLink = (article) => {
  const path = article?.path?.trim();
  return path && /^https?:\/\//i.test(path) ? path : null;
};

// { article } for a single news post
export const useNewsArticle = (id) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  return useQuery({
    queryKey: [`newsArticle${lang}`, lang, id],
    queryFn: () => fetchV2(endpoit.GetArticle, { articleId: id, langId: langId(lang) }),
    enabled: Boolean(id),
  });
};

// Accepts a full YouTube URL (watch, youtu.be, embed, shorts) or a bare video id.
export const getYoutubeId = (path) => {
  if (!path) return null;
  const match = path.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/
  );
  if (match) return match[1];
  return /^[\w-]{11}$/.test(path.trim()) ? path.trim() : null;
};

const isFacebookVideo = (path) =>
  /^https?:\/\/(www\.|m\.|web\.)?(facebook\.com\/(.+\/videos\/|watch|reel\/|share\/[rv]\/)|fb\.watch\/)/i.test(
    path?.trim() ?? ""
  );

// Embeddable player URL for an article's `path` (YouTube or Facebook), or null.
export const getVideoEmbedUrl = (path) => {
  const youtubeId = getYoutubeId(path);
  if (youtubeId) return `https://www.youtube.com/embed/${youtubeId}`;
  if (isFacebookVideo(path)) {
    // Drop tracking params like ?mibextid=…; Facebook only needs the video URL
    const url = path.trim().replace(/\?.*$/, "");
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  }
  return null;
};

// The article's own uploaded image, or null when the CMS has none ("noimg").
// Arabic titles currently come back as "?????" in the image name, and no file exists for them.
export const getArticleImage = (article) =>
  article?.thumbURL && article.thumbURL !== "noimg" && !article.thumbURL.includes("?")
    ? `${process.env.REACT_APP_BASE_IMAGE}${article.fullImagePath}_1920x1080.webp`
    : null;

export const isNewsFallbackImage = (src) => src === fallbackImage;

// <img onError> fallback for news images that fail to load.
export const onNewsImageError = (e) => {
  if (e.currentTarget.dataset.fallback) return;
  e.currentTarget.dataset.fallback = "1";
  e.currentTarget.classList.add("is-fallback");
  e.currentTarget.src = fallbackImage;
};

export const getNewsImage = (article) => {
  const youtubeId = getYoutubeId(article?.path);
  const articleImage = getArticleImage(article);
  if (articleImage) return articleImage;
  if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  return fallbackImage;
};

// Plain-text teaser from the API's `veryShortDescription`, else from the description HTML.
export const getNewsExcerpt = (article, length = 140) => {
  const source = article?.veryShortDescription || article?.description || "";
  const text = source
    .replace(/<[^>]*>/g, " ")
    .replace(/https?:\/\/\S+/g, " ") // bare links read as noise in a teaser
    .replace(/\s+/g, " ")
    .trim();
  return text.length > length ? `${text.slice(0, length).trim()}…` : text;
};

export const formatNewsDate = (date, lang) =>
  date
    ? new Date(date).toLocaleDateString(lang === "ar" ? "ar-JO" : "en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
